'use client';

import { useEffect, useRef, useState } from 'react';

const MIN_BPM = 40;
const MAX_BPM = 240;
const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_S = 0.1;

const TIME_SIGNATURES = [
  { value: '2', label: '2 / 4' },
  { value: '3', label: '3 / 4' },
  { value: '4', label: '4 / 4' },
  { value: '5', label: '5 / 4' },
  { value: '6', label: '6 / 8' },
  { value: '7', label: '7 / 8' },
];

export default function BrowserMetronome() {
  const [bpm, setBpm] = useState(120);
  const [beatsPerBar, setBeatsPerBar] = useState(4);
  const [isRunning, setIsRunning] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(-1);
  const [error, setError] = useState<string | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);
  const nextNoteTimeRef = useRef(0);
  const beatInBarRef = useRef(0);
  const bpmRef = useRef(bpm);
  const beatsPerBarRef = useRef(beatsPerBar);

  useEffect(() => {
    bpmRef.current = bpm;
  }, [bpm]);

  useEffect(() => {
    beatsPerBarRef.current = beatsPerBar;
  }, [beatsPerBar]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null && typeof window !== 'undefined') {
        window.clearInterval(timerRef.current);
      }
      if (audioContextRef.current) {
        void audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  function playClick(context: AudioContext, time: number, accented: boolean) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = accented ? 1500 : 900;
    gain.gain.setValueAtTime(accented ? 0.9 : 0.55, time);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.05);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(time);
    oscillator.stop(time + 0.06);
  }

  function scheduler() {
    const context = audioContextRef.current;
    if (!context) {
      return;
    }
    while (nextNoteTimeRef.current < context.currentTime + SCHEDULE_AHEAD_S) {
      const beat = beatInBarRef.current;
      const scheduledTime = nextNoteTimeRef.current;
      playClick(context, scheduledTime, beat === 0);

      const delayMs = Math.max(0, (scheduledTime - context.currentTime) * 1000);
      const beatToShow = beat;
      window.setTimeout(() => {
        setCurrentBeat(beatToShow);
      }, delayMs);

      const secondsPerBeat = 60 / bpmRef.current;
      nextNoteTimeRef.current += secondsPerBeat;
      beatInBarRef.current = (beat + 1) % beatsPerBarRef.current;
    }
  }

  async function start() {
    if (typeof window === 'undefined') {
      return;
    }
    setError(null);
    try {
      let context = audioContextRef.current;
      if (!context) {
        const AudioCtx =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioCtx) {
          setError('Your browser does not support the Web Audio API.');
          return;
        }
        context = new AudioCtx();
        audioContextRef.current = context;
      }
      if (context.state === 'suspended') {
        await context.resume();
      }
      beatInBarRef.current = 0;
      nextNoteTimeRef.current = context.currentTime + 0.05;
      setCurrentBeat(-1);
      setIsRunning(true);
      timerRef.current = window.setInterval(scheduler, LOOKAHEAD_MS);
    } catch {
      setError('The browser could not start audio playback.');
    }
  }

  function stop() {
    if (timerRef.current !== null && typeof window !== 'undefined') {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRunning(false);
    setCurrentBeat(-1);
  }

  function toggle() {
    if (isRunning) {
      stop();
    } else {
      void start();
    }
  }

  const beatDots = Array.from({ length: beatsPerBar }, (_, index) => index);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Metronome</span>
        <h2 className="tool-live-title">Online metronome</h2>
        <p className="tool-live-subtitle">
          Keep steady time with an accurate Web Audio metronome. Set the tempo and time signature,
          hit start, and follow the accented downbeat. Everything runs in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Tempo: {bpm} BPM</span>
              <input
                max={MAX_BPM}
                min={MIN_BPM}
                onChange={(event) => setBpm(Number(event.target.value))}
                type="range"
                value={bpm}
              />
            </label>
            <label className="tool-live-field">
              <span>Beats per bar</span>
              <select
                onChange={(event) => setBeatsPerBar(Number(event.target.value))}
                value={String(beatsPerBar)}
              >
                {TIME_SIGNATURES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>BPM (exact)</span>
              <input
                max={MAX_BPM}
                min={MIN_BPM}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  if (Number.isFinite(next)) {
                    setBpm(Math.min(MAX_BPM, Math.max(MIN_BPM, Math.round(next))));
                  }
                }}
                type="number"
                value={bpm}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={toggle} type="button">
              {isRunning ? 'Stop' : 'Start'}
            </button>
          </div>
          {error ? <p className="status-message status-error">{error}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.6rem',
              justifyContent: 'center',
              padding: '1.75rem 1rem',
            }}
          >
            {beatDots.map((index) => {
              const active = index === currentBeat;
              const isDownbeat = index === 0;
              return (
                <span
                  aria-hidden="true"
                  key={index}
                  style={{
                    width: '2.6rem',
                    height: '2.6rem',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    color: active ? '#ffffff' : '#6b7280',
                    background: active
                      ? isDownbeat
                        ? '#3b5bdb'
                        : '#2f9e44'
                      : 'rgba(148, 163, 184, 0.2)',
                    transition: 'background 60ms linear, color 60ms linear',
                  }}
                >
                  {index + 1}
                </span>
              );
            })}
          </div>
          <p className="tool-live-panel-note">
            The blue dot marks the accented downbeat of each bar. Tempo can be changed while the
            metronome is running.
          </p>
        </section>
      </div>
    </div>
  );
}
